import { useState, useMemo, useEffect, useId } from 'react';
import { useSessionState } from '@/hooks/useSessionState';
import { useLocalFilter } from '@/hooks/useLocalFilter';
import { SessionKey } from '@/types/storage';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { useScreenState } from '@/hooks/useScreenState';
import { useToast } from '@/hooks/useToast';
import { useUserGroupsQuery, useOrgUsersQuery } from '@/hooks/queries';
import { SearchInput } from '@/components/core';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { ScreenType } from '@/types/screen.types';
import { generateDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { useThemeColors } from '@/styles';
import { withOpacity } from '@/styles/colors/withOpacity';
import { spacing, opacity } from '@/styles/design-tokens';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AsyncButton } from '@/components/ui/async-button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Icon, IconName } from '@/icons';
import { logger } from '@/utils/logger';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { executeQuery } from '@/lib/execute-query';
import type {
  GetUserGroupMembersInput,
  GetUserGroupMembersOutput,
  CreateUserGroupInput,
  CreateUserGroupOutput,
  DeleteUserGroupInput,
  DeleteUserGroupOutput,
  BulkAddUserGroupMembersInput,
  BulkAddUserGroupMembersOutput,
  RemoveUserGroupMemberInput,
  RemoveUserGroupMemberOutput,
  GetUserGroupsGroupsItem,
  GetUserGroupMembersMembersItem,
} from '@/types/generated';

// ─── Query key ───────────────────────────────────────────────────────────────

const groupMembersKey = (groupId: string) =>
  ['user_group_members', groupId] as const;

const GROUP_SEARCH_KEYS: Array<keyof GetUserGroupsGroupsItem> = [
  'name',
  'description',
];
const USER_SEARCH_KEYS: Array<
  keyof { id: string; username: string; display_name: string }
> = ['display_name', 'username'];

// ─── Screen ──────────────────────────────────────────────────────────────────

export default function Groups() {
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const colors = useThemeColors();
  const toast = useToast();

  // Only superadmin org users may delete groups
  const canDeleteGroup = user?.organization?.is_superadmin_org === true;

  // ── Data ────────────────────────────────────────────────────────────────────
  const { data: groupsData, isLoading: isGroupsLoading } =
    useUserGroupsQuery(activeOrganizationId);
  const groups = groupsData?.groups ?? [];

  const { data: orgUsersData } = useOrgUsersQuery(activeOrganizationId);
  const orgUsers = orgUsersData?.users ?? [];

  // ── Selection ───────────────────────────────────────────────────────────────
  const [selectedGroupId, setSelectedGroupId] = useSessionState<string | null>(
    SessionKey.LB_GROUPS_SELECTED_GROUP_ID,
    null
  );
  const selectedGroup = groups.find(g => g.id === selectedGroupId) ?? null;

  // Local search — filters data already in frontend state, no extra API call
  const [groupSearch, setGroupSearch] = useState('');
  const filteredGroups = [
    ...useLocalFilter(groups, groupSearch, GROUP_SEARCH_KEYS),
  ].sort((a, b) => a.name.localeCompare(b.name));

  const [memberSearch, setMemberSearch] = useState('');

  // Only the group creator or a superadmin org user may manage members
  const canManageMembers =
    user?.organization?.is_superadmin_org === true ||
    selectedGroup?.created_by === user?.id;

  // Members for selected group (fetched on demand)
  const { data: membersData, isLoading: isMembersLoading } = useQuery({
    queryKey: selectedGroupId
      ? groupMembersKey(selectedGroupId)
      : ['group_members_none'],
    queryFn: () =>
      executeQuery<GetUserGroupMembersInput, GetUserGroupMembersOutput>(
        Action.GET_USER_GROUP_MEMBERS,
        { group_id: selectedGroupId! }
      ),
    enabled: !!selectedGroupId,
    staleTime: 1000 * 60,
  });
  const members = membersData?.members ?? [];

  // ── UI State ────────────────────────────────────────────────────────────────
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [stagedUserIds, setStagedUserIds] = useState<string[]>([]);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  // ── Orchestrators ───────────────────────────────────────────────────────────
  const { execute: executeCreate, isLoading: isCreating } = useOrchestrator<
    CreateUserGroupInput,
    CreateUserGroupOutput
  >(Action.CREATE_USER_GROUP);

  const { execute: executeDelete, isLoading: isDeleting } = useOrchestrator<
    DeleteUserGroupInput,
    DeleteUserGroupOutput
  >(Action.DELETE_USER_GROUP);

  const { execute: executeBulkAddMembers, isLoading: isAddingMembers } =
    useOrchestrator<
      BulkAddUserGroupMembersInput,
      BulkAddUserGroupMembersOutput
    >(Action.BULK_ADD_USER_GROUP_MEMBERS);

  const { execute: executeRemoveMember } = useOrchestrator<
    RemoveUserGroupMemberInput,
    RemoveUserGroupMemberOutput
  >(Action.REMOVE_USER_GROUP_MEMBER);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const availableUsers = useMemo(() => {
    const memberIds = new Set(members.map(m => m.user_id));
    const stagedIds = new Set(stagedUserIds);
    return orgUsers.filter(u => !memberIds.has(u.id) && !stagedIds.has(u.id));
  }, [orgUsers, members, stagedUserIds]);

  const filteredAvailableUsers = [
    ...useLocalFilter(availableUsers, memberSearch, USER_SEARCH_KEYS),
  ].sort((a, b) => a.display_name.localeCompare(b.display_name));

  const stagedUsers = useMemo(
    () =>
      stagedUserIds
        .map(id => orgUsers.find(u => u.id === id))
        .filter(Boolean) as Array<{
        id: string;
        username: string;
        display_name: string;
      }>,
    [stagedUserIds, orgUsers]
  );

  // ── Invalidation helpers ────────────────────────────────────────────────────
  const invalidateGroups = () => {
    if (!activeOrganizationId) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.userGroups.forOrg(activeOrganizationId),
    });
  };

  const invalidateMembers = () => {
    if (selectedGroupId) {
      queryClient.invalidateQueries({
        queryKey: groupMembersKey(selectedGroupId),
      });
    }
    // Cohorts reads eligibility via the bulk key (['user_group_members', 'bulk', ids]),
    // which the per-group key above never matches — invalidate the whole prefix too.
    queryClient.invalidateQueries({
      predicate: query => query.queryKey[0] === 'user_group_members',
    });
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleCreateGroup = async () => {
    if (!createName.trim() || !activeOrganizationId) return;
    const result = await executeCreate({
      organization_id: activeOrganizationId,
      name: createName.trim(),
      ...(createDescription.trim()
        ? { description: createDescription.trim() }
        : {}),
    });
    if (
      result.code === ResponseCode.SUCCESS ||
      result.code === ResponseCode.CREATED
    ) {
      toast.success('Group created');
      setIsCreateOpen(false);
      setCreateName('');
      setCreateDescription('');
      invalidateGroups();
    } else if (result.code === ResponseCode.DUPLICATE_RESOURCE) {
      toast.error('A group with this name already exists');
    } else {
      toast.error('Failed to create group');
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    const result = await executeDelete({ group_id: groupId });
    if (result.code === ResponseCode.SUCCESS) {
      toast.success('Group deleted');
      setDeletingGroupId(null);
      if (selectedGroupId === groupId) setSelectedGroupId(null);
      invalidateGroups();
    } else {
      toast.error('Failed to delete group');
      setDeletingGroupId(null);
    }
  };

  const handleStageUser = (userId: string) => {
    if (!userId) return;
    setStagedUserIds(prev => [...prev, userId]);
  };

  const handleUnstageUser = (userId: string) => {
    setStagedUserIds(prev => prev.filter(id => id !== userId));
  };

  const handleSaveMembers = async () => {
    if (!selectedGroupId || stagedUserIds.length === 0) return;
    const result = await executeBulkAddMembers({
      group_id: selectedGroupId,
      user_ids: stagedUserIds,
    });
    if (
      result.code === ResponseCode.SUCCESS ||
      result.code === ResponseCode.CREATED
    ) {
      const count = result.data?.added_count ?? stagedUserIds.length;
      toast.success(`${count} member${count !== 1 ? 's' : ''} added`);
      setStagedUserIds([]);
      invalidateMembers();
      invalidateGroups();
    } else {
      toast.error('Failed to add members');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedGroupId) return;
    const result = await executeRemoveMember({
      group_id: selectedGroupId,
      user_id: userId,
    });
    if (result.code === ResponseCode.SUCCESS) {
      toast.success('Member removed');
      invalidateMembers();
      invalidateGroups();
    } else {
      toast.error('Failed to remove member');
    }
  };

  // ── Screen state ─────────────────────────────────────────────────────────────
  useScreenState({
    metadata: {
      screenType: ScreenType.GROUPS,
      requiresAuth: true,
      showHeader: true,
      showSidebar: true,
    },
    onLoad: async (_strategy, state) => {
      logger.info('Groups: Loading');
      state.setSuccess();
    },
    lifecycle: {
      onUnmount: () => {
        logger.info('Groups unmounted');
      },
    },
  });

  if (!user) return null;

  return (
    <div
      className="flex h-full min-h-0"
      style={{
        padding: spacing.lg,
        gap: spacing.lg,
        color: colors.text.primary,
      }}
    >
      {/* ── Left: Groups list ─────────────────────────────────────────────── */}
      <div
        className="flex flex-col flex-shrink-0"
        style={{ width: '280px', gap: spacing.md }}
      >
        <div className="flex items-center justify-between">
          <span
            className="text-xs font-semibold tracking-wider uppercase"
            style={{ color: colors.text.secondary }}
          >
            Groups
          </span>
          <AsyncButton
            onClick={async () => setIsCreateOpen(true)}
            icon={IconName.PLUS}
            size="sm"
            variant="outline"
          >
            New
          </AsyncButton>
        </div>

        {groups.length > 0 && (
          <SearchInput
            value={groupSearch}
            onChange={setGroupSearch}
            placeholder="Search groups..."
          />
        )}

        <div
          className="flex flex-col flex-1 overflow-y-auto min-h-0"
          style={{ gap: spacing.sm }}
        >
          {isGroupsLoading ? (
            <span className="text-sm" style={{ color: colors.text.secondary }}>
              Loading...
            </span>
          ) : groups.length === 0 ? (
            <div
              className="flex flex-col items-center gap-2 py-4 text-center"
              style={{ color: colors.text.tertiary }}
            >
              <Icon
                name={IconName.USERS}
                size="xl"
                color={colors.text.tertiary}
              />
              <span
                className="text-sm font-medium"
                style={{ color: colors.text.secondary }}
              >
                {canDeleteGroup ? 'No groups yet' : 'No groups to show'}
              </span>
              <span className="text-xs">
                {canDeleteGroup
                  ? 'Create one using the button above.'
                  : 'Ask an admin to create a group and add you as a member.'}
              </span>
            </div>
          ) : filteredGroups.length === 0 ? (
            <span className="text-sm" style={{ color: colors.text.tertiary }}>
              No groups match "{groupSearch}"
            </span>
          ) : (
            filteredGroups.map(group => (
              <GroupListItem
                key={group.id}
                group={group}
                isSelected={group.id === selectedGroupId}
                isConfirmingDelete={deletingGroupId === group.id}
                canDelete={canDeleteGroup}
                isDeleting={isDeleting}
                onClick={() => {
                  setSelectedGroupId(group.id);
                  setStagedUserIds([]);
                  setMemberSearch('');
                }}
                onDeleteClick={() => setDeletingGroupId(group.id)}
                onDeleteConfirm={() => handleDeleteGroup(group.id)}
                onDeleteCancel={() => setDeletingGroupId(null)}
                colors={colors}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Divider ───────────────────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 w-px self-stretch"
        style={{ backgroundColor: colors.border.default }}
      />

      {/* ── Right: Group detail ───────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0" style={{ gap: spacing.lg }}>
        {!selectedGroup ? (
          <div
            className="flex flex-col items-center justify-center h-full gap-2"
            style={{ color: colors.text.secondary }}
          >
            <Icon
              name={IconName.USERS}
              size="xl"
              color={colors.text.secondary}
            />
            <span className="text-sm font-medium">
              Select a group to view its members
            </span>
            <span
              className="text-xs text-center"
              style={{ color: colors.text.tertiary, maxWidth: '220px' }}
            >
              Pick a group from the left to see who's in it and manage
              membership.
            </span>
          </div>
        ) : (
          <GroupDetail
            group={selectedGroup}
            members={members}
            isMembersLoading={isMembersLoading}
            availableUsers={filteredAvailableUsers}
            hasAvailableUsers={availableUsers.length > 0}
            memberSearch={memberSearch}
            onMemberSearchChange={setMemberSearch}
            stagedUsers={stagedUsers}
            onStageUser={handleStageUser}
            onUnstageUser={handleUnstageUser}
            onSaveMembers={handleSaveMembers}
            isAddingMembers={isAddingMembers}
            onRemoveMember={handleRemoveMember}
            canManageMembers={canManageMembers}
            colors={colors}
          />
        )}
      </div>

      {/* ── Create Group Dialog ───────────────────────────────────────────── */}
      <Dialog
        open={isCreateOpen}
        onOpenChange={open => {
          if (!open) {
            setIsCreateOpen(false);
            setCreateName('');
            setCreateDescription('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Group</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col" style={{ gap: spacing.md }}>
            <div className="flex flex-col" style={{ gap: spacing.sm }}>
              <Label htmlFor="group-name">Name</Label>
              <Input
                id="group-name"
                value={createName}
                onChange={e => setCreateName(e.target.value)}
                placeholder="Group name"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter' && createName.trim())
                    handleCreateGroup();
                }}
              />
            </div>
            <div className="flex flex-col" style={{ gap: spacing.sm }}>
              <Label htmlFor="group-description">Description (optional)</Label>
              <Input
                id="group-description"
                value={createDescription}
                onChange={e => setCreateDescription(e.target.value)}
                placeholder="What is this group for?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateOpen(false);
                setCreateName('');
                setCreateDescription('');
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <AsyncButton
              onClick={handleCreateGroup}
              isLoading={isCreating}
              loadingText="Creating..."
              disabled={!createName.trim()}
            >
              Create
            </AsyncButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── GroupListItem ────────────────────────────────────────────────────────────

interface GroupListItemProps {
  group: GetUserGroupsGroupsItem;
  isSelected: boolean;
  isConfirmingDelete: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onClick: () => void;
  onDeleteClick: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
  colors: ReturnType<typeof useThemeColors>;
}

function GroupListItem({
  group,
  isSelected,
  isConfirmingDelete,
  canDelete,
  isDeleting,
  onClick,
  onDeleteClick,
  onDeleteConfirm,
  onDeleteCancel,
  colors,
}: GroupListItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  const bgColor = isSelected
    ? colors.active.background
    : isHovered
      ? withOpacity(colors.primary, 0.12)
      : 'transparent';

  const borderColor = isSelected
    ? colors.active.background
    : isHovered
      ? colors.primary
      : colors.border.default;

  return (
    <div
      className="rounded-lg border cursor-pointer transition-all duration-150"
      style={{
        borderColor,
        backgroundColor: bgColor,
        padding: spacing.md,
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
          <span
            className="text-sm font-medium truncate"
            style={{
              color: isSelected ? colors.active.text : colors.text.primary,
            }}
          >
            {group.name}
          </span>
          {group.description && (
            <span
              className="text-xs truncate"
              style={{
                color: isSelected
                  ? withOpacity(colors.active.text, 0.85)
                  : colors.text.secondary,
              }}
            >
              {group.description}
            </span>
          )}
          <span
            className="text-xs"
            style={{
              color: isSelected
                ? withOpacity(colors.active.text, 0.85)
                : colors.text.secondary,
            }}
          >
            {group.member_count}{' '}
            {group.member_count === 1 ? 'member' : 'members'}
          </span>
        </div>
        {canDelete && (
          <button
            type="button"
            className="flex-shrink-0 p-1 rounded opacity-50 hover:opacity-100 transition-opacity"
            style={{ color: colors.error }}
            onClick={e => {
              e.stopPropagation();
              onDeleteClick();
            }}
          >
            <Icon name={IconName.TRASH} size="sm" />
          </button>
        )}
      </div>

      {/* Inline delete confirmation */}
      {isConfirmingDelete && (
        <div
          className="flex items-center gap-2 mt-2 pt-2"
          style={{ borderTop: `1px solid ${colors.border.default}` }}
          onClick={e => e.stopPropagation()}
        >
          <span className="text-xs flex-1" style={{ color: colors.error }}>
            Delete this group?
          </span>
          <AsyncButton
            size="sm"
            variant="destructive"
            onClick={async () => onDeleteConfirm()}
            isLoading={isDeleting}
            loadingText="..."
          >
            Yes
          </AsyncButton>
          <Button
            size="sm"
            variant="outline"
            onClick={onDeleteCancel}
            disabled={isDeleting}
          >
            No
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── GroupDetail ──────────────────────────────────────────────────────────────

interface GroupDetailProps {
  group: GetUserGroupsGroupsItem;
  members: GetUserGroupMembersMembersItem[];
  isMembersLoading: boolean;
  availableUsers: Array<{ id: string; username: string; display_name: string }>;
  hasAvailableUsers: boolean;
  memberSearch: string;
  onMemberSearchChange: (value: string) => void;
  stagedUsers: Array<{ id: string; username: string; display_name: string }>;
  onStageUser: (userId: string) => void;
  onUnstageUser: (userId: string) => void;
  onSaveMembers: () => Promise<void>;
  isAddingMembers: boolean;
  onRemoveMember: (userId: string) => Promise<void>;
  canManageMembers: boolean;
  colors: ReturnType<typeof useThemeColors>;
}

function GroupDetail({
  group,
  members,
  isMembersLoading,
  availableUsers,
  hasAvailableUsers,
  memberSearch,
  onMemberSearchChange,
  stagedUsers,
  onStageUser,
  onUnstageUser,
  onSaveMembers,
  isAddingMembers,
  onRemoveMember,
  canManageMembers,
  colors,
}: GroupDetailProps) {
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);

  // The input unmounts once everyone is staged/added; don't let a stale "open"
  // flag resurrect the dropdown when it remounts later (e.g. after a removal).
  useEffect(() => {
    if (!hasAvailableUsers) setIsMemberDropdownOpen(false);
  }, [hasAvailableUsers]);

  // Multi-select combobox: the dropdown stays open after a pick (the input keeps
  // focus, so onFocus would never reopen it) and the list shrinks as users are staged.
  // activeIndex is set by arrow keys; until then the top match is the Enter target
  // once a query is typed.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listboxId = useId();
  const optionId = (userId: string) => `${listboxId}-${userId}`;

  const highlightIndex =
    availableUsers.length === 0
      ? -1
      : activeIndex !== null
        ? Math.min(activeIndex, availableUsers.length - 1)
        : memberSearch.trim() !== ''
          ? 0
          : -1;
  const highlightedUser =
    highlightIndex >= 0 ? availableUsers[highlightIndex] : undefined;

  const highlightedOptionId = highlightedUser
    ? optionId(highlightedUser.id)
    : undefined;

  useEffect(() => {
    if (!highlightedOptionId) return;
    document
      .getElementById(highlightedOptionId)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlightedOptionId]);

  const stageUser = (userId: string) => {
    onStageUser(userId);
    onMemberSearchChange('');
    setActiveIndex(null);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter confirming an IME composition (CJK input) must not stage anyone
    if (e.nativeEvent.isComposing) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isMemberDropdownOpen) {
        setIsMemberDropdownOpen(true);
        return;
      }
      const count = availableUsers.length;
      if (count === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const from = highlightIndex >= 0 ? highlightIndex : step === 1 ? -1 : 0;
      setActiveIndex((from + step + count) % count);
      return;
    }

    if (!isMemberDropdownOpen) return;
    if (e.key === 'Escape') {
      // Don't let a surrounding modal/panel also close on this Escape
      e.stopPropagation();
      setIsMemberDropdownOpen(false);
      setActiveIndex(null);
    } else if (e.key === 'Enter' && !isAddingMembers && highlightedUser) {
      // Only a visibly highlighted row is picked — never an arbitrary first user
      e.preventDefault();
      stageUser(highlightedUser.id);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0" style={{ gap: spacing.lg }}>
      {/* Group header */}
      <div className="flex flex-col" style={{ gap: spacing.xs }}>
        <h2
          className="text-lg font-semibold"
          style={{ color: colors.text.primary }}
        >
          {group.name}
        </h2>
        {group.description && (
          <p className="text-sm" style={{ color: colors.text.secondary }}>
            {group.description}
          </p>
        )}
        <p className="text-xs" style={{ color: colors.text.tertiary }}>
          Created by{' '}
          <a
            href={generateDeepLink(DeepLinkEntity.USER, group.created_by)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.primary, textDecoration: 'underline' }}
          >
            {group.creator_name}
          </a>
        </p>
      </div>

      {/* Add member section — only visible to group creator or superadmin org user */}
      {canManageMembers && (
        <div className="flex flex-col" style={{ gap: spacing.sm }}>
          {hasAvailableUsers ? (
            <div className="relative w-64">
              <SearchInput
                value={memberSearch}
                onChange={v => {
                  onMemberSearchChange(v);
                  setActiveIndex(null);
                  setIsMemberDropdownOpen(true);
                }}
                placeholder="Search people to add..."
                onFocus={() => setIsMemberDropdownOpen(true)}
                onClick={() => setIsMemberDropdownOpen(true)}
                onBlur={() => setIsMemberDropdownOpen(false)}
                onKeyDown={handleSearchKeyDown}
                inputProps={{
                  role: 'combobox',
                  'aria-expanded': isMemberDropdownOpen,
                  'aria-controls': listboxId,
                  'aria-autocomplete': 'list',
                  'aria-activedescendant': highlightedOptionId,
                }}
              />
              {isMemberDropdownOpen && (
                <div
                  id={listboxId}
                  role="listbox"
                  className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-md border shadow-md"
                  // keep input focus when grabbing the scrollbar / padding
                  onMouseDown={e => e.preventDefault()}
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border.default,
                  }}
                >
                  {availableUsers.length === 0 ? (
                    <div
                      role="presentation"
                      className="px-3 py-2 text-xs"
                      style={{ color: colors.text.tertiary }}
                    >
                      No matches for "{memberSearch}"
                    </div>
                  ) : (
                    availableUsers.map((u, index) => (
                      <button
                        key={u.id}
                        id={optionId(u.id)}
                        role="option"
                        aria-selected={index === highlightIndex}
                        tabIndex={-1}
                        type="button"
                        disabled={isAddingMembers}
                        className="flex w-full items-center px-3 py-2 text-sm text-left hover:opacity-80 disabled:opacity-50"
                        style={{
                          color: colors.text.primary,
                          // the highlighted row is what Enter will pick
                          backgroundColor:
                            index === highlightIndex
                              ? withOpacity(colors.primary, opacity.light)
                              : undefined,
                        }}
                        // focus is kept by the container's mousedown preventDefault,
                        // so selecting on click (not press) is safe and cancellable
                        onClick={() => stageUser(u.id)}
                      >
                        {u.display_name}{' '}
                        <span style={{ opacity: 0.6 }}>@{u.username}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : (
            <span className="text-xs" style={{ color: colors.text.tertiary }}>
              All org members are in this group
            </span>
          )}

          {stagedUsers.length > 0 && (
            <div
              className="flex flex-wrap items-center"
              style={{ gap: spacing.xs }}
            >
              {stagedUsers.map(u => (
                <div
                  key={u.id}
                  className="flex items-center gap-1 rounded-full text-xs font-medium"
                  style={{
                    padding: `2px ${spacing.sm} 2px ${spacing.sm}`,
                    backgroundColor: colors.active.background,
                    color: colors.active.text,
                    border: `1px solid ${colors.primary}`,
                  }}
                >
                  {u.display_name}
                  <button
                    type="button"
                    onClick={() => onUnstageUser(u.id)}
                    disabled={isAddingMembers}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginLeft: 'calc(2px * var(--sizing-multiplier, 1))',
                    }}
                  >
                    <Icon name={IconName.X} size="sm" />
                  </button>
                </div>
              ))}
              <AsyncButton
                onClick={onSaveMembers}
                isLoading={isAddingMembers}
                loadingText="Saving..."
                size="sm"
              >
                Save {stagedUsers.length} member
                {stagedUsers.length !== 1 ? 's' : ''}
              </AsyncButton>
            </div>
          )}
        </div>
      )}

      {/* Members list */}
      <div
        className="flex flex-col flex-1 overflow-y-auto min-h-0"
        style={{ gap: spacing.sm }}
      >
        <span
          className="text-xs font-semibold tracking-wider uppercase"
          style={{ color: colors.text.secondary }}
        >
          Members ({members.length})
        </span>

        {isMembersLoading ? (
          <span className="text-sm" style={{ color: colors.text.secondary }}>
            Loading members...
          </span>
        ) : members.length === 0 ? (
          <div
            className="flex flex-col items-center gap-2 py-4 text-center"
            style={{ color: colors.text.tertiary }}
          >
            <Icon
              name={IconName.USER_SEARCH}
              size="xl"
              color={colors.text.tertiary}
            />
            <span className="text-sm" style={{ color: colors.text.secondary }}>
              No members yet
            </span>
            <span className="text-xs">Use the search above to add people.</span>
          </div>
        ) : (
          members.map(member => (
            <MemberRow
              key={member.id}
              member={member}
              canRemove={canManageMembers}
              onRemove={() => onRemoveMember(member.user_id)}
              colors={colors}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── MemberRow ────────────────────────────────────────────────────────────────

interface MemberRowProps {
  member: GetUserGroupMembersMembersItem;
  canRemove: boolean;
  onRemove: () => Promise<void>;
  colors: ReturnType<typeof useThemeColors>;
}

function MemberRow({ member, canRemove, onRemove, colors }: MemberRowProps) {
  return (
    <div
      className="flex items-center justify-between rounded-lg px-3 py-2 border"
      style={{ borderColor: colors.border.default }}
    >
      <div className="flex flex-col min-w-0">
        <span
          className="text-sm font-medium truncate"
          style={{ color: colors.text.primary }}
        >
          {member.display_name}
        </span>
        <span className="text-xs" style={{ color: colors.text.secondary }}>
          @{member.username} · {member.role}
        </span>
      </div>
      {canRemove && (
        <AsyncButton
          onClick={onRemove}
          size="sm"
          variant="outline"
          icon={IconName.X}
          loadingText="..."
        >
          Remove
        </AsyncButton>
      )}
    </div>
  );
}
